import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "./client";

export interface DependencyCheck {
  status: "ok" | "error";
  latency_ms: number;
  error?: string;
}

export interface ReadinessReport {
  status: "ok" | "degraded";
  checks: Record<string, DependencyCheck>;
}

export interface HealthReport {
  status: string;
  version: string;
}

function isReadinessReport(value: unknown): value is ReadinessReport {
  return (
    typeof value === "object" &&
    value !== null &&
    "checks" in value &&
    typeof (value as ReadinessReport).checks === "object"
  );
}

/**
 * `/ready` responde **503 com corpo** quando alguma dependência está fora — é o
 * que os orquestradores esperam. Para a tela de status, porém, esse corpo é
 * justamente o dado mais útil: recuperamos o relatório do erro em vez de
 * descartá-lo, senão a página diria apenas "falhou", sem dizer o quê.
 */
export function useReadiness() {
  return useQuery({
    queryKey: ["readiness"],
    queryFn: async (): Promise<ReadinessReport> => {
      try {
        return await apiFetch<ReadinessReport>("/ready");
      } catch (error) {
        if (error instanceof ApiError && isReadinessReport(error.payload)) {
          return error.payload;
        }
        throw error;
      }
    },
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: () => apiFetch<HealthReport>("/health"),
    staleTime: 60_000,
  });
}
